// Insurance cover: upload the insurer's schedule and see, car by car, whether
// every active car is on cover and every car that has left the fleet has come
// off it. db/migrations/9900, supabase/functions/insurance-cover-read.
//
// Every outcome and sentence on this page comes from Postgres
// (insurance_cover_compare_v1, v_insurance_cover_checks). React only chooses a
// colour and what to fold away.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ExternalLink, FileUp, ShieldCheck, UploadCloud } from 'lucide-react'
import {
  useGetInsuranceCover,
  useOpenInsuranceSchedule,
  useSetAsideInsuranceSchedule,
  useUploadInsuranceSchedule,
} from '../hooks/backend/insurance'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Spinner } from '../lib/shadcn/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../lib/shadcn/table'
import { Chip, type ChipTone } from '../components/interviews/Chip'
import { fileToBase64 } from '../utils/marketing'
import { formatDate, formatDateTime } from '../utils/interviews'

type CoverRow = {
  row_key: string
  vehicle_id: string | null
  registration: string | null
  vehicle: string | null
  vehicle_status: string | null
  commercial_model: string | null
  left_on: string | null
  expected: string | null
  lines_on_cover: number
  policies: string | null
  printed_as: string | null
  outcome: string
  severity: 'urgent' | 'review' | 'ok'
  sentence: string
}

type Check = {
  id: string
  filename: string
  source: string
  status: 'reading' | 'read' | 'failed'
  outcome: string | null
  insurer: string | null
  policy_number: string | null
  schedule_date: string | null
  reader_warnings: string[] | null
  uploaded_at: string
  set_aside_at: string | null
  set_aside_reason: string | null
  set_aside_by_name: string | null
  uploaded_by_name: string | null
  has_file: boolean
  is_current: boolean
  lines: number
  lines_matched: number
  staleness: string | null
}

type Rule = { vehicle_status: string; expected: string; severity: string; reason: string }

type Cover = {
  access: { can_upload: boolean; uploaders: string | null; linked: boolean }
  check_id: string | null
  rows: CoverRow[]
  checks: Check[]
  unchecked: { policy_number: string; insurer: string; sentence: string }[]
  rules: Rule[]
}

const OUTCOME: Record<string, { label: string; tone: ChipTone }> = {
  not_covered: { label: 'Not covered', tone: 'red' },
  covered_twice: { label: 'Covered twice', tone: 'red' },
  still_covered: { label: 'Still covered', tone: 'red' },
  not_in_fleet: { label: 'Not in fleet', tone: 'red' },
  unidentified_line: { label: 'No car named', tone: 'red' },
  leaving_still_covered: { label: 'Leaving, still covered', tone: 'amber' },
  no_rule: { label: 'No rule', tone: 'amber' },
  covered: { label: 'Covered', tone: 'green' },
  off_cover: { label: 'Off cover', tone: 'neutral' },
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  offboarding: 'Offboarding',
  offboarded: 'Offboarded',
  written_off: 'Written off',
}

const CHECK_TONE: Record<string, ChipTone> = { reading: 'amber', read: 'green', failed: 'red' }

// A Postgres date reaches the browser as a full timestamp string; only the day
// is meant.
const day = (s: string | null) => (s ? formatDate(s.slice(0, 10)) : '—')

export default function InsuranceCover() {
  const cover = useGetInsuranceCover<Cover | null>()
  const upload = useUploadInsuranceSchedule<{ check_id: string; status: string; duplicate: boolean }>()
  const open = useOpenInsuranceSchedule<{ url: string; filename: string }>()
  const setAside = useSetAsideInsuranceSchedule<{ ok: boolean }>()

  const [checkId, setCheckId] = useState<string | null>(null)
  const [sending, setSending] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ tone: ChipTone; text: string } | null>(null)
  const [showOk, setShowOk] = useState(false)
  const [asideFor, setAsideFor] = useState<string | null>(null)
  const [asideReason, setAsideReason] = useState('')
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const inputRef = useRef<HTMLInputElement>(null)

  const load = cover.trigger
  const refresh = useCallback(
    () => { void load({ check_id: checkId }, { skipCache: true }).catch(() => undefined) },
    [load, checkId],
  )
  useEffect(() => { refresh() }, [refresh])

  const data = cover.data
  const rows = data?.rows ?? []
  const checks = data?.checks ?? []
  const canUpload = data?.access.can_upload === true

  // Follow a schedule while it is being read.
  const reading = checks.some((c) => c.status === 'reading')
  useEffect(() => {
    if (!reading) return
    const t = setInterval(refresh, 4000)
    return () => clearInterval(t)
  }, [reading, refresh])

  const attention = useMemo(() => rows.filter((r) => r.severity !== 'ok'), [rows])
  const inOrder = useMemo(() => rows.filter((r) => r.severity === 'ok'), [rows])
  const urgent = attention.filter((r) => r.severity === 'urgent').length
  const covered = inOrder.filter((r) => r.outcome === 'covered').length
  const current = checks.filter((c) => c.is_current)
  const comparing = checkId ? checks.find((c) => c.id === checkId) ?? null : null

  const send = async (file: File) => {
    setNotice(null)
    setSending(file.name)
    try {
      if (file.size > 8 * 1024 * 1024) throw new Error('That file is larger than 8 MB. Ask the insurer for the emailed PDF rather than a scan.')
      const contentBase64 = await fileToBase64(file)
      const res = await upload.trigger({ filename: file.name, contentType: file.type, contentBase64 })
      setCheckId(null)
      setNotice(res?.duplicate
        ? { tone: 'neutral', text: 'This exact file was uploaded before, so nothing new was read. Its check is in the list below.' }
        : { tone: 'amber', text: `${file.name} is being read. The check below updates by itself when it is done.` })
      refresh()
    } catch (e) {
      setNotice({ tone: 'red', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setSending(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const openFile = async (c: Check) => {
    // Opened before the await, so the browser does not treat it as a popup.
    const w = window.open('', '_blank')
    try {
      const res = await open.trigger({ check_id: c.id })
      if (w && res?.url) w.location.href = res.url
      else w?.close()
    } catch (e) {
      w?.close()
      setRowError((m) => ({ ...m, [c.id]: e instanceof Error ? e.message : String(e) }))
    }
  }

  const confirmAside = async (c: Check) => {
    try {
      await setAside.trigger({ check_id: c.id, reason: asideReason })
      setAsideFor(null)
      setAsideReason('')
      if (checkId === c.id) setCheckId(null)
      refresh()
    } catch (e) {
      setRowError((m) => ({ ...m, [c.id]: e instanceof Error ? e.message : String(e) }))
    }
  }

  // The app shell hides overflow, so a page scrolls only if it owns a
  // scrolling box of its own that fills the space it is given.
  return (
    <div className="h-full min-h-0 overflow-y-auto">
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><ShieldCheck className="h-6 w-6" /> Insurance cover</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Upload the insurer's schedule as they sent it: a PDF, a photo or a spreadsheet. Every car in the fleet is checked
          against it. Active cars should be on cover. Cars that have been offboarded or written off should be off it. A car
          still being offboarded comes off once it is handed back.
        </p>
      </header>

      <section className="space-y-2 rounded-lg border p-4" aria-label="Upload a schedule">
        {canUpload ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept=".pdf,.xlsx,.xls,.csv,image/*,application/pdf"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void send(f) }}
              />
              <Button onClick={() => inputRef.current?.click()} disabled={sending !== null}>
                {sending ? <Spinner className="mr-2 h-4 w-4" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                {sending ? `Uploading ${sending}…` : 'Upload insurance schedule'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Password protected PDF: open it with the password, choose Print, then Save as PDF, and upload that copy.
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {data?.access.linked === false
              ? 'Your login is not linked to a person in cockpit access, so you can read this check but not upload. Ask an admin to link it.'
              : `Uploading a schedule is open to ${data?.access.uploaders ?? 'the insurance checkers'}. You can read the check below.`}
          </p>
        )}
        {notice && <p className="text-sm"><Chip tone={notice.tone}>{notice.text}</Chip></p>}
      </section>

      {cover.loading && !data && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the check…</div>}
      {cover.error && <p className="text-sm text-red-600">{cover.error}</p>}

      {data && (
        <>
          {comparing && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
              <span>
                Comparing the fleet with one schedule alone: <strong>{comparing.insurer ?? comparing.filename}</strong>
                {comparing.policy_number ? `, ${comparing.policy_number}` : ''}, {day(comparing.schedule_date)}.
                Cars on other policies will show as not covered.
              </span>
              <Button size="sm" variant="outline" onClick={() => setCheckId(null)}>Back to all current schedules</Button>
            </div>
          )}

          {!comparing && (data.unchecked.length > 0 || current.some((c) => c.staleness) || current.length === 0) && (
            <ul className="space-y-2" aria-label="Before you trust this check">
              {current.length === 0 && (
                <li className="flex gap-2 text-sm"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />No schedule is on record yet, so every active car shows as not covered. Upload the current schedule.</li>
              )}
              {data.unchecked.map((u) => (
                <li key={u.policy_number} className="flex gap-2 text-sm"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />{u.sentence}</li>
              ))}
              {current.filter((c) => c.staleness).map((c) => (
                <li key={c.id} className="flex gap-2 text-sm"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />{c.policy_number ? `${c.policy_number}: ` : ''}{c.staleness}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 text-sm">
            <Chip tone={urgent > 0 ? 'red' : 'green'}>{urgent} to fix</Chip>
            <Chip tone={attention.length - urgent > 0 ? 'amber' : 'neutral'}>{attention.length - urgent} to review</Chip>
            <Chip tone="green">{covered} active and covered</Chip>
            <Chip tone="neutral">{inOrder.length - covered} off the fleet and off cover</Chip>
          </div>

          <section className="space-y-2" aria-label="What needs doing">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              {attention.length > 0
                ? <><AlertTriangle className="h-5 w-5 text-red-600" /> What needs doing</>
                : <><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Nothing needs doing</>}
            </h2>
            {attention.length > 0 && <CoverTable rows={attention} />}
          </section>

          <section className="space-y-2" aria-label="In order">
            <Button variant="outline" size="sm" onClick={() => setShowOk((s) => !s)}>
              {showOk ? 'Hide' : 'Show'} the {inOrder.length} cars that are in order
            </Button>
            {showOk && <CoverTable rows={inOrder} />}
          </section>

          <section className="space-y-2" aria-label="Schedules on record">
            <h2 className="flex items-center gap-2 text-lg font-semibold"><FileUp className="h-5 w-5" /> Schedules on record</h2>
            <p className="text-sm text-muted-foreground">
              The check uses the newest schedule for each policy. Set a schedule aside when the policy is no longer held or the
              wrong file was uploaded; it stays here with who set it aside and why.
            </p>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Dated</TableHead>
                    <TableHead>Cars</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checks.map((c) => (
                    <TableRow key={c.id} className={c.set_aside_at ? 'opacity-60' : undefined}>
                      <TableCell className="max-w-[320px] align-top">
                        <div className="font-medium">{c.insurer ?? '—'}{c.policy_number ? `, ${c.policy_number}` : ''}</div>
                        <div className="break-words text-xs text-muted-foreground">{c.filename}</div>
                        {(c.reader_warnings ?? []).length > 0 && (
                          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-700 dark:text-amber-300">
                            {(c.reader_warnings ?? []).map((w, i) => <li key={i}>{w}</li>)}
                          </ul>
                        )}
                      </TableCell>
                      <TableCell className="align-top whitespace-nowrap">{day(c.schedule_date)}</TableCell>
                      <TableCell className="align-top whitespace-nowrap tabular-nums">
                        {c.status === 'read' ? `${c.lines} (${c.lines_matched} in fleet)` : '—'}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        <div>{formatDateTime(c.uploaded_at)}</div>
                        <div className="text-muted-foreground">{c.uploaded_by_name || (c.source === 'seed' ? 'Typed in by Yolisa' : '—')}</div>
                      </TableCell>
                      <TableCell className="max-w-[280px] align-top text-xs">
                        <div className="flex flex-wrap gap-1">
                          <Chip tone={CHECK_TONE[c.status] ?? 'neutral'}>{c.status === 'reading' ? 'Reading' : c.status === 'read' ? 'Read' : 'Could not read'}</Chip>
                          {c.is_current && <Chip tone="brand">In the check</Chip>}
                          {c.set_aside_at && <Chip tone="neutral">Set aside</Chip>}
                        </div>
                        {c.outcome && <div className="mt-1">{c.outcome}</div>}
                        {c.set_aside_at && (
                          <div className="mt-1 text-muted-foreground">
                            Set aside {formatDate(c.set_aside_at.slice(0, 10))}{c.set_aside_by_name ? ` by ${c.set_aside_by_name}` : ''}: {c.set_aside_reason}
                          </div>
                        )}
                        {rowError[c.id] && <div className="mt-1 text-red-600">{rowError[c.id]}</div>}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex flex-col items-start gap-1">
                          {c.has_file && (
                            <Button size="sm" variant="ghost" onClick={() => void openFile(c)}>
                              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open
                            </Button>
                          )}
                          {c.status === 'read' && checkId !== c.id && (
                            <Button size="sm" variant="ghost" onClick={() => setCheckId(c.id)}>Check this one alone</Button>
                          )}
                          {canUpload && !c.set_aside_at && c.status !== 'reading' && (
                            asideFor === c.id ? (
                              <div className="flex flex-col gap-1">
                                <Input
                                  className="h-8 w-[240px] text-xs"
                                  placeholder="Why, e.g. policy cancelled"
                                  value={asideReason}
                                  onChange={(e) => setAsideReason(e.target.value)}
                                  autoFocus
                                />
                                <div className="flex gap-1">
                                  <Button size="sm" onClick={() => void confirmAside(c)} disabled={!asideReason.trim()}>Set aside</Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setAsideFor(null); setAsideReason('') }}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => { setAsideFor(c.id); setAsideReason('') }}>Set aside</Button>
                            )
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          <section className="space-y-1 text-sm text-muted-foreground" aria-label="The rules">
            <h2 className="text-base font-semibold text-foreground">The rules the check applies</h2>
            <ul className="list-disc space-y-0.5 pl-5">
              {data.rules.map((r) => (
                <li key={r.vehicle_status}>
                  <strong>{STATUS_LABEL[r.vehicle_status] ?? r.vehicle_status}</strong>: {r.expected === 'on' ? 'on cover' : 'off cover'}. {r.reason}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
    </div>
  )
}

function CoverTable({ rows }: { rows: CoverRow[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Car</TableHead>
            <TableHead>Fleet status</TableHead>
            <TableHead>On the schedule</TableHead>
            <TableHead className="hidden md:table-cell">What to do</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const o = OUTCOME[r.outcome] ?? { label: r.outcome, tone: 'neutral' as ChipTone }
            return (
              <TableRow key={r.row_key}>
                <TableCell className="align-top">
                  <div className="font-medium">{r.registration ?? 'No registration'}</div>
                  {r.vehicle && <div className="text-xs text-muted-foreground">{r.vehicle}</div>}
                  {/* On a phone the sentence sits under the car rather than off the right edge. */}
                  <div className="mt-1 max-w-[260px] text-xs md:hidden">{r.sentence}</div>
                </TableCell>
                <TableCell className="align-top whitespace-nowrap text-sm">
                  {r.vehicle_status ? STATUS_LABEL[r.vehicle_status] ?? r.vehicle_status : 'Not in the fleet'}
                  {r.left_on && <div className="text-xs text-muted-foreground">left {formatDate(r.left_on.slice(0, 10))}</div>}
                </TableCell>
                <TableCell className="align-top text-sm">
                  <Chip tone={o.tone}>{o.label}</Chip>
                  {r.policies && <div className="mt-1 text-xs text-muted-foreground">{r.policies}</div>}
                </TableCell>
                <TableCell className="hidden align-top text-sm md:table-cell">{r.sentence}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
